import {
  PublicMarketplaceCertificate,
  PublicMarketplaceConfig,
  PublicMarketplaceLanguage,
  PublicMarketplaceLevel,
  PublicMarketplaceModule,
} from '@/@types/PublicMarketplace'
import { ProductDetailsResponse, ProductRole } from '@/@types/Products'
import RenderSkeleton from '@/components/Products/edit-product-skeleton'
import CustomSelect from '@/components/shared/CustomSelect/CustomSelect'
import { Input } from '@/components/shared/Input/Input'
import { Label } from '@/components/shared/Label/Label'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useAuth } from '@/auth/Auth'
import { toast } from '@/hooks/use-toast'
import { userInitials } from '@/lib/utils'
import { CheckoutsService } from '@/services/checkouts.service'
import { OffersService } from '@/services/offers.service'
import { ProductsService } from '@/services/products.service'
import { ProfileService } from '@/services/profile.service'
import { PublicMarketplaceService } from '@/services/public-marketplace.service'
import {
  normalizeUrlOnBlur,
  normalizeUrlWhenPaste,
} from '@/utils/normalize'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertCircle,
  CheckCircle2,
  ClipboardPaste,
  ExternalLink,
  Eye,
  Loader2,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext, useParams } from 'react-router-dom'
import { PublicMarketplacePreviewDialog } from './PublicMarketplacePreviewDialog'

const LEVEL_OPTIONS: { value: PublicMarketplaceLevel; labelKey: string }[] = [
  { value: 'beginner', labelKey: 'publicMarketplaceLevelBeginner' },
  { value: 'intermediate', labelKey: 'publicMarketplaceLevelIntermediate' },
  { value: 'advanced', labelKey: 'publicMarketplaceLevelAdvanced' },
]

const LANGUAGE_OPTIONS: {
  value: PublicMarketplaceLanguage
  labelKey: string
}[] = [
  { value: 'pt', labelKey: 'publicMarketplaceLanguagePt' },
  { value: 'en', labelKey: 'publicMarketplaceLanguageEn' },
  { value: 'es', labelKey: 'publicMarketplaceLanguageEs' },
  { value: 'de', labelKey: 'publicMarketplaceLanguageDe' },
  { value: 'fr', labelKey: 'publicMarketplaceLanguageFr' },
  { value: 'it', labelKey: 'publicMarketplaceLanguageIt' },
  { value: 'ja', labelKey: 'publicMarketplaceLanguageJa' },
]

const CERTIFICATE_OPTIONS: {
  value: PublicMarketplaceCertificate
  labelKey: string
}[] = [
  { value: 'included', labelKey: 'publicMarketplaceCertificateIncluded' },
  { value: 'none', labelKey: 'publicMarketplaceCertificateNone' },
]

const AREA_CLIENT_BASE_URL = (
  (import.meta.env.VITE_AREA_CLIENT_URL as string | undefined) ||
  'https://orders.mundpay.com'
).replace(/\/$/, '')

const PRODUCT_IMAGE_MAX_BYTES = 4 * 1024 * 1024
const PRODUCT_IMAGE_MAX_MB = 4
const PRODUCT_IMAGE_MIN_WIDTH = 800

type FormState = {
  enabled: boolean
  offer_id: string
  checkout_id: string
  level: PublicMarketplaceLevel | ''
  language: PublicMarketplaceLanguage | ''
  certificate: PublicMarketplaceCertificate | ''
  advantages: string[]
  modules: PublicMarketplaceModule[]
  biography: string
  instagram_url: string
  facebook_url: string
  youtube_url: string
  photo_url: string | null
}

const emptyForm = (): FormState => ({
  enabled: false,
  offer_id: '',
  checkout_id: '',
  level: '',
  language: '',
  certificate: '',
  advantages: [],
  modules: [],
  biography: '',
  instagram_url: '',
  facebook_url: '',
  youtube_url: '',
  photo_url: null,
})

function normalizeOptionalUrl(value: string): string | null {
  const normalized = normalizeUrlOnBlur(value || '')
  if (!normalized || normalized === 'https://' || normalized === 'http://') {
    return null
  }
  return normalized
}

function serializeModulesPaste(modules: PublicMarketplaceModule[]): string {
  return modules
    .filter((module) => module.title.trim() || module.description?.trim())
    .map((module) =>
      [
        module.title.trim(),
        (module.description || '').trim(),
        ...(module.subtopics ?? []).map((item) => item.trim()).filter(Boolean),
      ].join('\n'),
    )
    .join('\n---\n')
}

function parseModulesPaste(text: string): PublicMarketplaceModule[] {
  return text
    .split(/\r?\n\s*---\s*\r?\n/)
    .map((block) => block.trim())
    .filter(Boolean)
    .map((block, index) => {
      const lines = block.split(/\r?\n/).map((line) => line.trimEnd())
      while (lines.length > 0 && lines[lines.length - 1] === '') {
        lines.pop()
      }

      const title = (lines[0] || '').trim()
      const description = (lines[1] || '').trim()
      const subtopics = lines
        .slice(2)
        .map((line) => line.trim())
        .filter(Boolean)

      return {
        title,
        description,
        subtopics,
        position: index,
      }
    })
    .filter((module) => module.title)
}

function mapConfigToForm(config?: PublicMarketplaceConfig | null): FormState {
  if (!config) return emptyForm()
  return {
    enabled: Boolean(config.enabled),
    offer_id: config.offer_id || '',
    checkout_id: config.checkout_id || '',
    level: config.level || '',
    language: config.language || '',
    certificate: config.certificate || '',
    advantages: config.advantages?.length ? [...config.advantages] : [],
    modules: config.modules?.length
      ? config.modules.map((module, index) => {
          const legacySubtopic =
            typeof (module as { subtopic?: string | null }).subtopic === 'string'
              ? (module as { subtopic?: string | null }).subtopic?.trim() || ''
              : ''
          const subtopics = (module.subtopics ?? [])
            .map((item) => item.trim())
            .filter(Boolean)

          return {
            id: module.id,
            title: module.title || '',
            subtopics:
              subtopics.length > 0
                ? subtopics
                : legacySubtopic
                  ? [legacySubtopic]
                  : [],
            description: module.description || '',
            position: module.position ?? index,
          }
        })
      : [],
    biography: config.producer?.biography || '',
    instagram_url: config.producer?.instagram_url || '',
    facebook_url: config.producer?.facebook_url || '',
    youtube_url: config.producer?.youtube_url || '',
    photo_url: config.producer?.photo_url || null,
  }
}

export default function PublicMarketplace() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation(['common', 'validation'])
  const { authData } = useAuth()
  const queryClient = useQueryClient()
  const photoInputRef = useRef<HTMLInputElement>(null)
  const productImageInputRef = useRef<HTMLInputElement>(null)
  const productImagePreviewUrlRef = useRef<string | null>(null)
  const hasHydratedForm = useRef(false)
  const { product } = useOutletContext<{ product: ProductDetailsResponse }>()
  const [form, setForm] = useState<FormState>(emptyForm())
  const [attemptedSubmit, setAttemptedSubmit] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [photoCacheKey, setPhotoCacheKey] = useState(0)
  const [productImagePreview, setProductImagePreview] = useState<string | null>(
    null,
  )
  const [productImageCacheKey, setProductImageCacheKey] = useState(0)
  const [productImageTooSmall, setProductImageTooSmall] = useState(false)
  const [productCoverBroken, setProductCoverBroken] = useState(false)
  const [advantagesPasteOpen, setAdvantagesPasteOpen] = useState(false)
  const [advantagesPasteText, setAdvantagesPasteText] = useState('')
  const [modulesPasteOpen, setModulesPasteOpen] = useState(false)
  const [modulesPasteText, setModulesPasteText] = useState('')

  const isProducer = product?.relationship?.role === ProductRole.PRODUCER
  const offerRequiredMessage = t('common:publicMarketplaceOfferRequired')
  const checkoutRequiredMessage = t('common:publicMarketplaceCheckoutRequired')
  const offerError =
    attemptedSubmit && !form.offer_id ? offerRequiredMessage : undefined
  const checkoutError =
    attemptedSubmit && !form.checkout_id ? checkoutRequiredMessage : undefined

  const { data: configResponse, isLoading } = useQuery({
    queryKey: ['public-marketplace', id],
    queryFn: () => PublicMarketplaceService.getPublicMarketplace(id!),
    enabled: Boolean(id) && isProducer,
    refetchOnMount: 'always',
  })

  const { data: offersResponse } = useQuery({
    queryKey: ['offers', id, 1],
    queryFn: () => OffersService.getOffers({ productId: id!, page: 1 }),
    enabled: Boolean(id) && isProducer,
  })

  const { data: checkoutsResponse } = useQuery({
    queryKey: ['checkouts', id, 1],
    queryFn: () => CheckoutsService.getCheckouts({ productId: id!, page: 1 }),
    enabled: Boolean(id) && isProducer,
    refetchOnMount: 'always',
  })

  useEffect(() => {
    hasHydratedForm.current = false
  }, [id])

  useEffect(() => {
    if (!configResponse || hasHydratedForm.current) return
    setForm(mapConfigToForm(configResponse.data))
    hasHydratedForm.current = true
  }, [configResponse])

  const offerOptions = useMemo(
    () =>
      (offersResponse?.data ?? []).map((offer: any) => ({
        value: String(offer.uuid || offer.id),
        label: offer.name,
      })),
    [offersResponse],
  )

  const checkoutOptions = useMemo(() => {
    if (!form.offer_id) return []

    return (checkoutsResponse?.data ?? [])
      .filter((checkout) => {
        if (!checkout.is_active) return false
        const offerUuid = String(checkout.offer?.uuid || checkout.offer?.id || '')
        return offerUuid === form.offer_id
      })
      .map((checkout) => ({
        value: String(checkout.id),
        label: checkout.name,
      }))
  }, [checkoutsResponse, form.offer_id])

  const status = configResponse?.data?.status || 'disabled'
  const isPending = status === 'pending'
  const isRejected = status === 'rejected'
  const marketplaceProductUrl = id
    ? `${AREA_CLIENT_BASE_URL}/marketplace/${id}`
    : null

  const inactiveCheckoutWarning = useMemo(() => {
    if (!checkoutsResponse) return false

    // Se o form já tem um checkout ativo selecionado, não bloqueia a UI
    if (form.checkout_id) {
      const selectedCheckout = (checkoutsResponse.data ?? []).find(
        (checkout) => String(checkout.id) === String(form.checkout_id),
      )
      if (selectedCheckout?.is_active) return false
    }

    const savedCheckoutId = configResponse?.data?.checkout_id
    if (savedCheckoutId) {
      const savedCheckout = (checkoutsResponse.data ?? []).find(
        (checkout) => String(checkout.id) === String(savedCheckoutId),
      )
      if (!savedCheckout?.is_active) return true
    }

    // Oferta escolhida sem nenhum checkout ativo disponível
    if (form.offer_id && checkoutOptions.length === 0) return true

    return false
  }, [
    checkoutsResponse,
    configResponse?.data?.checkout_id,
    form.checkout_id,
    form.offer_id,
    checkoutOptions.length,
  ])

  const isLiveOnMarketplace =
    status === 'approved' &&
    Boolean(configResponse?.data?.enabled) &&
    !inactiveCheckoutWarning

  // Checkout vinculado inativo: desliga o switch e limpa o select inválido.
  useEffect(() => {
    if (!hasHydratedForm.current || !checkoutsResponse) return

    setForm((prev) => {
      const checkouts = checkoutsResponse.data ?? []
      const selectedCheckout = prev.checkout_id
        ? checkouts.find(
            (checkout) => String(checkout.id) === String(prev.checkout_id),
          )
        : undefined
      const selectedIsInactive = Boolean(prev.checkout_id) && !selectedCheckout?.is_active

      const savedCheckoutId = configResponse?.data?.checkout_id
      const savedCheckout = savedCheckoutId
        ? checkouts.find(
            (checkout) => String(checkout.id) === String(savedCheckoutId),
          )
        : undefined
      const savedIsInactive = Boolean(savedCheckoutId) && !savedCheckout?.is_active
      const formStillOnSavedInactive =
        savedIsInactive &&
        (!prev.checkout_id ||
          String(prev.checkout_id) === String(savedCheckoutId))

      if (!selectedIsInactive && !formStillOnSavedInactive) return prev

      const nextCheckoutId = selectedIsInactive ? '' : prev.checkout_id
      const nextEnabled =
        selectedIsInactive || formStillOnSavedInactive ? false : prev.enabled

      if (prev.enabled === nextEnabled && prev.checkout_id === nextCheckoutId) {
        return prev
      }

      return {
        ...prev,
        enabled: nextEnabled,
        checkout_id: nextCheckoutId,
      }
    })
  }, [checkoutsResponse, configResponse?.data?.checkout_id])

  const { mutate: save, isPending: isSaving } = useMutation({
    mutationFn: () =>
      PublicMarketplaceService.upsertPublicMarketplace(id!, {
        enabled: form.enabled,
        offer_id: form.offer_id || null,
        checkout_id: form.checkout_id || null,
        level: form.level || null,
        language: form.language || null,
        certificate: form.certificate || null,
        advantages: form.advantages.filter((item) => item.trim()),
        modules: form.modules
          .filter((module) => module.title.trim())
          .map((module, index) => ({
            id: module.id,
            title: module.title.trim(),
            subtopics: (module.subtopics ?? [])
              .map((item) => item.trim())
              .filter(Boolean),
            description: module.description?.trim() || null,
            position: index,
          })),
        producer: {
          biography: form.biography || null,
          instagram_url: normalizeOptionalUrl(form.instagram_url),
          facebook_url: normalizeOptionalUrl(form.facebook_url),
          youtube_url: normalizeOptionalUrl(form.youtube_url),
        },
      }),
    onSuccess: (response) => {
      setAttemptedSubmit(false)
      queryClient.setQueryData(['public-marketplace', id], response)
      toast({
        variant: 'success',
        description: t('common:publicMarketplaceSaved'),
      })
    },
    onError: (error: any) => {
      toast({
        variant: 'error',
        description:
          error?.response?.data?.message || t('common:data_submission_error'),
      })
    },
  })

  const handleSave = () => {
    setAttemptedSubmit(true)
    if (!form.offer_id) {
      toast({
        variant: 'error',
        description: offerRequiredMessage,
      })
      document
        .querySelector('[data-cy="select-public-marketplace-offer"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    if (!form.checkout_id) {
      toast({
        variant: 'error',
        description: checkoutRequiredMessage,
      })
      document
        .querySelector('[data-cy="select-public-marketplace-checkout"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    save()
  }

  const { mutate: uploadPhoto, isPending: isUploadingPhoto } = useMutation({
    mutationFn: (file: File) => ProfileService.uploadProfilePhoto(file),
    onSuccess: async (response: any) => {
      const uploadedUrl =
        response?.data?.profile_photo?.url ||
        response?.profile_photo?.url ||
        null

      if (uploadedUrl) {
        setForm((prev) => ({ ...prev, photo_url: uploadedUrl }))
        queryClient.setQueryData(
          ['public-marketplace', id],
          (current: { data: PublicMarketplaceConfig } | undefined) => {
            if (!current?.data) return current
            return {
              ...current,
              data: {
                ...current.data,
                producer: {
                  ...current.data.producer,
                  photo_url: uploadedUrl,
                },
              },
            }
          },
        )
      }
      setPhotoCacheKey(Date.now())

      await queryClient.invalidateQueries({ queryKey: ['profile'] })
      await queryClient.invalidateQueries({ queryKey: ['user-info'] })
      toast({
        variant: 'success',
        description: t('common:publicMarketplacePhotoUpdated'),
      })
    },
    onError: (error) => {
      toast({
        variant: 'error',
        description: ProfileService.getProfilePhotoUploadErrorMessage(error, t),
      })
    },
  })

  const handlePhotoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (file.size > ProfileService.PROFILE_PHOTO_MAX_BYTES) {
      toast({
        variant: 'error',
        description: t('common:file_too_large', {
          size: ProfileService.PROFILE_PHOTO_MAX_MB,
        }),
      })
      event.target.value = ''
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        setForm((prev) => ({ ...prev, photo_url: reader.result as string }))
        setPhotoCacheKey(Date.now())
      }
    }
    reader.readAsDataURL(file)

    uploadPhoto(file, {
      onSettled: () => {
        event.target.value = ''
      },
    })
  }

  const { mutate: uploadProductImage, isPending: isUploadingProductImage } =
    useMutation({
      mutationFn: (image: File) =>
        ProductsService.uploadProductImage({ image, productId: id! }),
      onSuccess: async (response: ProductDetailsResponse) => {
        if (response?.images) {
          queryClient.setQueryData(
            [`get-product-${id}`],
            (current: ProductDetailsResponse | undefined) =>
              current
                ? {
                    ...current,
                    images: response.images,
                  }
                : current,
          )
        }

        await queryClient.invalidateQueries({
          queryKey: [`get-product-${id}`],
        })
        setProductImageCacheKey(Date.now())
        toast({
          variant: 'success',
          description: t('common:publicMarketplaceCoverUpdated'),
        })
      },
      onError: (error: any) => {
        toast({
          variant: 'error',
          description:
            error?.response?.data?.message || t('common:data_submission_error'),
        })
      },
    })

  const checkProductImageDimensions = (src: string) => {
    const image = new Image()
    image.onload = () => {
      setProductImageTooSmall(image.naturalWidth < PRODUCT_IMAGE_MIN_WIDTH)
    }
    image.onerror = () => {
      setProductImageTooSmall(false)
    }
    image.src = src
  }

  const handleProductImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (file.size > PRODUCT_IMAGE_MAX_BYTES) {
      toast({
        variant: 'error',
        description: t('common:file_too_large', {
          size: PRODUCT_IMAGE_MAX_MB,
        }),
      })
      event.target.value = ''
      return
    }

    if (productImagePreviewUrlRef.current) {
      URL.revokeObjectURL(productImagePreviewUrlRef.current)
    }

    const previewUrl = URL.createObjectURL(file)
    productImagePreviewUrlRef.current = previewUrl
    setProductImagePreview(previewUrl)
    setProductImageCacheKey(Date.now())
    checkProductImageDimensions(previewUrl)

    uploadProductImage(file, {
      onSettled: () => {
        event.target.value = ''
      },
    })
  }

  useEffect(() => {
    return () => {
      if (productImagePreviewUrlRef.current) {
        URL.revokeObjectURL(productImagePreviewUrlRef.current)
      }
    }
  }, [])

  const producerPhotoSrc = form.photo_url
    ? form.photo_url.startsWith('data:')
      ? form.photo_url
      : `${form.photo_url}${form.photo_url.includes('?') ? '&' : '?'}v=${photoCacheKey || '1'}`
    : undefined

  const productCoverSrc = productImagePreview
    ? productImagePreview
    : product?.images?.[0]?.url
      ? `${product.images[0].url}${
          product.images[0].url.includes('?') ? '&' : '?'
        }v=${productImageCacheKey || '1'}`
      : null

  useEffect(() => {
    setProductCoverBroken(false)
    if (!productCoverSrc) {
      setProductImageTooSmall(false)
      return
    }
    checkProductImageDimensions(productCoverSrc)
  }, [productCoverSrc])

  if (!id || !isProducer) return null

  const categoryName =
    configResponse?.data?.category?.name || product?.category?.name || ''
  const description =
    configResponse?.data?.description || product?.description || ''
  const sellerName =
    authData?.account?.name?.trim() || authData?.name?.trim() || ''
  const productTypeLabel =
    product?.category?.type === 'physical' ||
    product?.category?.type === 'pharmaceutical'
      ? t('common:physicalProduct')
      : t('common:digital')
  const levelOption = LEVEL_OPTIONS.find((option) => option.value === form.level)
  const languageOption = LANGUAGE_OPTIONS.find(
    (option) => option.value === form.language,
  )
  const certificateOption = CERTIFICATE_OPTIONS.find(
    (option) => option.value === form.certificate,
  )
  const previewDetails = [
    { label: t('common:category'), value: categoryName },
    { label: t('common:productType'), value: productTypeLabel },
    {
      label: t('common:publicMarketplaceLevel'),
      value: levelOption ? t(`common:${levelOption.labelKey}`) : '',
    },
    {
      label: t('common:publicMarketplaceLanguage'),
      value: languageOption ? t(`common:${languageOption.labelKey}`) : '',
    },
    {
      label: t('common:publicMarketplaceCertificate'),
      value: certificateOption ? t(`common:${certificateOption.labelKey}`) : '',
    },
  ]

  return (
    <>
      <section className="m-auto max-w-[1032px] md:px-10 xl:px-0 w-full mb-[120px]">
        {isRejected && (
          <Alert
            variant="destructive"
            className="mt-12 mb-2 bg-[#211315]"
          >
            <AlertCircle color="#ef4444" className="h-5 w-5 flex-shrink-0" />
            <div>
              <AlertTitle className="text-[#ef4444]">
                {t('common:publicMarketplaceRejectedTitle')}
              </AlertTitle>
              <AlertDescription className="text-muted-foreground">
                {configResponse?.data?.rejection_reason ||
                  t('common:publicMarketplaceRejectedMessage')}
              </AlertDescription>
            </div>
          </Alert>
        )}

        {isPending && (
          <Alert
            variant="default"
            className="mt-12 mb-2 border-amber-500/30 bg-amber-500/10"
          >
            <AlertCircle className="h-5 w-5 flex-shrink-0 text-amber-400" />
            <div>
              <AlertTitle className="text-amber-400">
                {t('common:publicMarketplacePendingTitle')}
              </AlertTitle>
              <AlertDescription className="text-muted-foreground">
                {t('common:publicMarketplacePendingMessage')}
              </AlertDescription>
            </div>
          </Alert>
        )}

        {inactiveCheckoutWarning && (
          <Alert
            variant="default"
            className={`${isPending || isRejected ? 'mt-2' : 'mt-12'} mb-2 border-amber-500/30 bg-amber-500/10`}
          >
            <AlertCircle className="h-5 w-5 flex-shrink-0 text-amber-400" />
            <div>
              <AlertTitle className="text-amber-400">
                {t('common:publicMarketplaceInactiveCheckoutTitle')}
              </AlertTitle>
              <AlertDescription className="text-muted-foreground">
                {t('common:publicMarketplaceInactiveCheckoutMessage')}
              </AlertDescription>
            </div>
          </Alert>
        )}

        {isLiveOnMarketplace && marketplaceProductUrl && (
          <Alert
            variant="default"
            className={`${
              isPending || isRejected || inactiveCheckoutWarning
                ? 'mt-2'
                : 'mt-12'
            } mb-2 border-[#A9D342]/30 bg-[#A9D342]/10`}
          >
            <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-[#A9D342]" />
            <div className="min-w-0 flex-1">
              <AlertTitle className="text-[#A9D342]">
                {t('common:publicMarketplaceLiveTitle')}
              </AlertTitle>
              <AlertDescription className="text-muted-foreground">
                <span className="block">
                  {t('common:publicMarketplaceLiveMessage')}
                </span>
                <a
                  href={marketplaceProductUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[#A9D342] hover:text-[#b8e055] underline-offset-4 hover:underline"
                  data-cy="public-marketplace-live-link"
                >
                  {t('common:publicMarketplaceLiveCta')}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </AlertDescription>
            </div>
          </Alert>
        )}

        <section className="gap-6 justify-between m-0 w-full flex-col lg:flex-row flex mt-12">
          <article className="space-y-1 lg:w-[328px]">
            <h2 className="text-colorText font-medium">
              {t('common:publicMarketplaceStatusSection')}
            </h2>
            <p className="text-muted text-sm">
              {t('common:publicMarketplaceStatusSectionHelper')}
            </p>
          </article>

          <Card className="w-full p-4 lg:p-6 lg:max-w-[680px] bg-[09090b]">
            {isLoading ? (
              <RenderSkeleton count={4} />
            ) : (
              <section className="space-y-6">
                <article className="flex items-center gap-2 flex-wrap">
                  <Switch
                    id="public-marketplace-enabled"
                    checked={form.enabled}
                    onCheckedChange={(checked) =>
                      setForm((prev) => ({ ...prev, enabled: checked }))
                    }
                    data-cy="switch-public-marketplace"
                  />
                  <label
                    htmlFor="public-marketplace-enabled"
                    className="text-gray-300 text-sm"
                  >
                    {form.enabled
                      ? t('common:publicMarketplaceEnabled')
                      : t('common:publicMarketplaceDisabled')}
                  </label>
                  {isPending && (
                    <span className="inline-flex items-center rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs font-medium text-amber-400">
                      {t('common:publicMarketplaceInReview')}
                    </span>
                  )}
                </article>
                <p className="text-sm text-muted -mt-4">
                  {form.enabled
                    ? t('common:publicMarketplaceEnabledHelper')
                    : t('common:publicMarketplaceDisabledHelper')}
                </p>

                <div className="space-y-2">
                  <CustomSelect
                    label={t('common:publicMarketplaceOfferLabel')}
                    placeholder={t('common:publicMarketplaceOfferPlaceholder')}
                    options={offerOptions}
                    value={form.offer_id || undefined}
                    onValueChange={(value) =>
                      setForm((prev) => {
                        const nextCheckoutOptions = (
                          checkoutsResponse?.data ?? []
                        ).filter((checkout) => {
                          if (!checkout.is_active) return false
                          const offerUuid = String(
                            checkout.offer?.uuid || checkout.offer?.id || '',
                          )
                          return offerUuid === value
                        })
                        const checkoutStillValid = nextCheckoutOptions.some(
                          (checkout) => String(checkout.id) === prev.checkout_id,
                        )

                        return {
                          ...prev,
                          offer_id: value,
                          checkout_id: checkoutStillValid ? prev.checkout_id : '',
                        }
                      })
                    }
                    helperText={offerError}
                    hasError={Boolean(offerError)}
                    dataCy="select-public-marketplace-offer"
                  />
                  {!offerError && (
                    <p className="text-sm text-muted">
                      {t('common:publicMarketplaceOfferHelper')}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <CustomSelect
                    label={t('common:publicMarketplaceCheckoutLabel')}
                    placeholder={t('common:publicMarketplaceCheckoutPlaceholder')}
                    options={checkoutOptions}
                    value={form.checkout_id || undefined}
                    onValueChange={(value) =>
                      setForm((prev) => ({ ...prev, checkout_id: value }))
                    }
                    helperText={checkoutError}
                    hasError={Boolean(checkoutError)}
                    disabled={!form.offer_id}
                    dataCy="select-public-marketplace-checkout"
                  />
                  {!checkoutError && (
                    <p className="text-sm text-muted">
                      {form.offer_id && checkoutOptions.length === 0
                        ? t('common:publicMarketplaceCheckoutEmpty')
                        : t('common:publicMarketplaceCheckoutHelper')}
                    </p>
                  )}
                </div>

                <Input
                  label={t('common:category')}
                  value={categoryName}
                  disabled
                  subLabel={t('common:publicMarketplaceFromGeralHelper')}
                  data-cy="public-marketplace-category"
                />

                <div className="space-y-2">
                  <Label htmlFor="public-marketplace-description">
                    {t('common:productDescriptionLabel')}
                  </Label>
                  <Textarea
                    id="public-marketplace-description"
                    value={description}
                    disabled
                    className="focus:outline-none ring-0 focus-visible:outline-none border-colorBorder min-h-[100px]"
                    data-cy="public-marketplace-description"
                  />
                  <p className="text-sm text-zinc-500">
                    {t('common:publicMarketplaceFromGeralHelper')}
                  </p>
                </div>
              </section>
            )}
          </Card>
        </section>

        <section className="gap-6 justify-between m-0 w-full flex-col lg:flex-row flex mt-12">
          <article className="space-y-1 lg:w-[328px]">
            <h2 className="text-colorText font-medium">
              {t('common:publicMarketplaceCoverSection')}
            </h2>
            <p className="text-muted text-sm">
              {t('common:publicMarketplaceCoverSectionHelper')}
            </p>
          </article>

          <Card className="w-full p-4 lg:p-6 lg:max-w-[680px] bg-[09090b]">
            {isLoading ? (
              <RenderSkeleton count={2} />
            ) : (
              <section className="space-y-4">
                <div
                  className="relative flex aspect-video w-full flex-col items-center justify-center overflow-hidden rounded-2xl"
                  style={{
                    background:
                      'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 48%, #C4B5FD 100%)',
                  }}
                  data-cy="public-marketplace-cover-preview"
                >
                  {productCoverSrc && !productCoverBroken ? (
                    <img
                      src={productCoverSrc}
                      alt={product?.name || ''}
                      className="absolute inset-0 h-full w-full object-cover"
                      onError={() => setProductCoverBroken(true)}
                    />
                  ) : (
                    <>
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-sm">
                        <span className="text-xl font-bold tracking-wide text-violet-600">
                          {userInitials(product?.name || 'MP')}
                        </span>
                      </div>
                      <p className="mt-3 max-w-[80%] text-center text-sm font-medium text-violet-700">
                        {t('common:publicMarketplaceCoverEmpty')}
                      </p>
                    </>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <label
                    htmlFor="public-marketplace-cover"
                    className={`bg-elevated px-4 py-2 rounded-lg text-sm text-colorText h-9 inline-flex items-center ${
                      isUploadingProductImage
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer'
                    }`}
                  >
                    {isUploadingProductImage ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('common:uploading')}
                      </>
                    ) : productCoverSrc ? (
                      t('common:publicMarketplaceCoverChange')
                    ) : (
                      t('common:publicMarketplaceCoverUpload')
                    )}
                  </label>
                  <input
                    id="public-marketplace-cover"
                    ref={productImageInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/jpg"
                    className="hidden"
                    disabled={isUploadingProductImage}
                    onChange={handleProductImageChange}
                    data-cy="public-marketplace-cover-input"
                  />
                </div>

                {productImageTooSmall && (
                  <Alert
                    variant="default"
                    className="border-amber-500/30 bg-amber-500/10"
                  >
                    <AlertCircle className="h-4 w-4 flex-shrink-0 text-amber-400" />
                    <AlertDescription className="text-muted-foreground text-sm">
                      {t('common:publicMarketplaceCoverSmallWarning')}
                    </AlertDescription>
                  </Alert>
                )}

                <ul className="space-y-1.5 text-sm text-muted list-disc pl-5">
                  <li>{t('common:publicMarketplaceCoverSpecRatio')}</li>
                  <li>{t('common:publicMarketplaceCoverSpecSize')}</li>
                  <li>{t('common:publicMarketplaceCoverSpecFormat')}</li>
                  <li>{t('common:publicMarketplaceCoverSpecTip')}</li>
                </ul>
                <p className="text-xs text-muted">
                  {t('common:publicMarketplaceCoverSharedHelper')}
                </p>
              </section>
            )}
          </Card>
        </section>

        <section className="gap-6 justify-between m-0 w-full flex-col lg:flex-row flex mt-12">
          <article className="space-y-1 lg:w-[328px]">
            <h2 className="text-colorText font-medium">
              {t('common:publicMarketplaceAdditionalSection')}
            </h2>
            <p className="text-muted text-sm">
              {t('common:publicMarketplaceAdditionalSectionHelper')}
            </p>
          </article>

          <Card className="w-full p-4 lg:p-6 lg:max-w-[680px] bg-[09090b]">
            {isLoading ? (
              <RenderSkeleton count={3} />
            ) : (
              <section className="space-y-6">
                <div className="flex gap-6 flex-col xl:flex-row">
                  <div className="w-full">
                    <CustomSelect
                      label={t('common:publicMarketplaceLevel')}
                      placeholder={t('common:select')}
                      options={LEVEL_OPTIONS.map((option) => ({
                        value: option.value,
                        label: t(`common:${option.labelKey}`),
                      }))}
                      value={form.level}
                      onValueChange={(value) =>
                        setForm((prev) => ({
                          ...prev,
                          level: value as PublicMarketplaceLevel,
                        }))
                      }
                    />
                  </div>
                  <div className="w-full">
                    <CustomSelect
                      label={t('common:publicMarketplaceLanguage')}
                      placeholder={t('common:select')}
                      options={LANGUAGE_OPTIONS.map((option) => ({
                        value: option.value,
                        label: t(`common:${option.labelKey}`),
                      }))}
                      value={form.language}
                      onValueChange={(value) =>
                        setForm((prev) => ({
                          ...prev,
                          language: value as PublicMarketplaceLanguage,
                        }))
                      }
                    />
                  </div>
                </div>

                <CustomSelect
                  label={t('common:publicMarketplaceCertificate')}
                  placeholder={t('common:select')}
                  options={CERTIFICATE_OPTIONS.map((option) => ({
                    value: option.value,
                    label: t(`common:${option.labelKey}`),
                  }))}
                  value={form.certificate}
                  onValueChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      certificate: value as PublicMarketplaceCertificate,
                    }))
                  }
                />

                <div className="space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <Label>{t('common:publicMarketplaceAdvantages')}</Label>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        className="bg-elevated px-4 py-2 rounded-lg text-sm text-colorText h-9 inline-flex items-center gap-2 shrink-0"
                        onClick={() => {
                          setAdvantagesPasteOpen((open) => {
                            if (!open) {
                              setAdvantagesPasteText(
                                form.advantages
                                  .map((item) => item.trim())
                                  .filter(Boolean)
                                  .join('\n'),
                              )
                            }
                            return !open
                          })
                        }}
                        data-cy="public-marketplace-paste-advantages"
                      >
                        <ClipboardPaste className="h-4 w-4" />
                        {advantagesPasteOpen
                          ? t('common:publicMarketplacePasteClose')
                          : t('common:publicMarketplacePasteAdvantages')}
                      </button>
                      {form.advantages.length === 0 && !advantagesPasteOpen && (
                        <button
                          type="button"
                          className="bg-elevated px-4 py-2 rounded-lg text-sm text-colorText h-9 inline-flex items-center gap-2 shrink-0"
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              advantages: [''],
                            }))
                          }
                        >
                          <Plus className="h-4 w-4" />
                          {t('common:publicMarketplaceAddAdvantage')}
                        </button>
                      )}
                    </div>
                  </div>

                  {advantagesPasteOpen && (
                    <div className="space-y-2 rounded-lg border border-colorBorder p-3">
                      <p className="text-sm text-muted">
                        {t('common:publicMarketplacePasteAdvantagesHelper')}
                      </p>
                      <Textarea
                        value={advantagesPasteText}
                        placeholder={t(
                          'common:publicMarketplacePasteAdvantagesPlaceholder',
                        )}
                        className="min-h-[120px] focus:outline-none ring-0 focus-visible:outline-none border-colorBorder"
                        onChange={(event) =>
                          setAdvantagesPasteText(event.target.value)
                        }
                        data-cy="public-marketplace-paste-advantages-textarea"
                      />
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => {
                            setAdvantagesPasteOpen(false)
                            setAdvantagesPasteText('')
                          }}
                        >
                          {t('common:cancel')}
                        </Button>
                        <Button
                          type="button"
                          onClick={() => {
                            const next = advantagesPasteText
                              .split(/\r?\n/)
                              .map((item) => item.trim())
                              .filter(Boolean)
                            setForm((prev) => ({
                              ...prev,
                              advantages: next.length > 0 ? next : [],
                            }))
                            setAdvantagesPasteOpen(false)
                            setAdvantagesPasteText('')
                          }}
                          data-cy="public-marketplace-apply-paste-advantages"
                        >
                          {t('common:publicMarketplacePasteAdvantagesApply')}
                        </Button>
                      </div>
                    </div>
                  )}

                  {form.advantages.map((advantage, index) => (
                    <div key={`advantage-${index}`} className="flex gap-2">
                      <Input
                        value={advantage}
                        onChange={(event) => {
                          const next = [...form.advantages]
                          next[index] = event.target.value
                          setForm((prev) => ({ ...prev, advantages: next }))
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="shrink-0"
                        aria-label="Remover"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            advantages: prev.advantages.filter(
                              (_, i) => i !== index,
                            ),
                          }))
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      {index === form.advantages.length - 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="shrink-0"
                          aria-label={t('common:publicMarketplaceAddAdvantage')}
                          onClick={() =>
                            setForm((prev) => ({
                              ...prev,
                              advantages: [...prev.advantages, ''],
                            }))
                          }
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </Card>
        </section>

        <section className="gap-6 justify-between m-0 w-full flex-col lg:flex-row flex mt-12">
          <article className="space-y-1 lg:w-[328px]">
            <h2 className="text-colorText font-medium">
              {t('common:publicMarketplaceContentSection')}
            </h2>
            <p className="text-muted text-sm">
              {t('common:publicMarketplaceContentSectionHelper')}
            </p>
          </article>

          <Card className="w-full p-4 lg:p-6 lg:max-w-[680px] bg-[09090b]">
            {isLoading ? (
              <RenderSkeleton count={3} />
            ) : (
              <section className="space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <Label>{t('common:publicMarketplaceModules')}</Label>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="bg-elevated px-4 py-2 rounded-lg text-sm text-colorText h-9 inline-flex items-center gap-2 shrink-0"
                      onClick={() => {
                        setModulesPasteOpen((open) => {
                          if (!open) {
                            setModulesPasteText(
                              serializeModulesPaste(form.modules),
                            )
                          }
                          return !open
                        })
                      }}
                      data-cy="public-marketplace-paste-modules"
                    >
                      <ClipboardPaste className="h-4 w-4" />
                      {modulesPasteOpen
                        ? t('common:publicMarketplacePasteClose')
                        : t('common:publicMarketplacePasteModules')}
                    </button>
                    {!modulesPasteOpen && (
                      <button
                        type="button"
                        className="bg-elevated px-4 py-2 rounded-lg text-sm text-colorText h-9 inline-flex items-center gap-2 shrink-0"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            modules: [
                              ...prev.modules,
                              {
                                title: '',
                                subtopics: [],
                                description: '',
                                position: prev.modules.length,
                              },
                            ],
                          }))
                        }
                      >
                        <Plus className="h-4 w-4" />
                        {t('common:publicMarketplaceAddModule')}
                      </button>
                    )}
                  </div>
                </div>

                {modulesPasteOpen && (
                  <div className="space-y-4 rounded-lg border border-colorBorder p-3">
                    <div className="space-y-3 text-sm text-muted">
                      <p className="font-medium text-colorText">
                        {t('common:publicMarketplacePasteModulesIntro')}
                      </p>
                      <div className="space-y-2">
                        <p>
                          {t('common:publicMarketplacePasteModulesFormatTitle')}
                        </p>
                        <ul className="space-y-1.5 pl-1">
                          <li>
                            <span className="font-medium text-colorText">
                              {t(
                                'common:publicMarketplacePasteModulesFormatLine1Label',
                              )}
                            </span>{' '}
                            {t(
                              'common:publicMarketplacePasteModulesFormatLine1Value',
                            )}
                          </li>
                          <li>
                            <span className="font-medium text-colorText">
                              {t(
                                'common:publicMarketplacePasteModulesFormatLine2Label',
                              )}
                            </span>{' '}
                            {t(
                              'common:publicMarketplacePasteModulesFormatLine2Value',
                            )}
                          </li>
                          <li>
                            <span className="font-medium text-colorText">
                              {t(
                                'common:publicMarketplacePasteModulesFormatRestLabel',
                              )}
                            </span>{' '}
                            {t(
                              'common:publicMarketplacePasteModulesFormatRestValue',
                            )}
                          </li>
                          <li className="flex items-start gap-2">
                            <code className="rounded bg-elevated px-1.5 py-0.5 font-mono text-xs text-colorText">
                              ---
                            </code>
                            <span>
                              {t(
                                'common:publicMarketplacePasteModulesFormatSeparator',
                              )}
                            </span>
                          </li>
                        </ul>
                      </div>
                      <div className="space-y-2">
                        <p className="font-medium text-colorText">
                          {t('common:publicMarketplacePasteModulesExampleLabel')}
                        </p>
                        <pre className="overflow-x-auto rounded-lg border border-colorBorder bg-elevated/40 p-3 font-mono text-xs leading-relaxed text-colorText whitespace-pre-wrap">
                          {t(
                            'common:publicMarketplacePasteModulesExample',
                          )}
                        </pre>
                      </div>
                      <p className="font-medium text-colorText">
                        {t('common:publicMarketplacePasteModulesApplyNote')}
                      </p>
                    </div>
                    <Textarea
                      value={modulesPasteText}
                      placeholder={t(
                        'common:publicMarketplacePasteModulesPlaceholder',
                      )}
                      className="min-h-[160px] focus:outline-none ring-0 focus-visible:outline-none border-colorBorder"
                      onChange={(event) =>
                        setModulesPasteText(event.target.value)
                      }
                      data-cy="public-marketplace-paste-modules-textarea"
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => {
                          setModulesPasteOpen(false)
                          setModulesPasteText('')
                        }}
                      >
                        {t('common:cancel')}
                      </Button>
                      <Button
                        type="button"
                        onClick={() => {
                          const next = parseModulesPaste(modulesPasteText)
                          setForm((prev) => ({
                            ...prev,
                            modules: next,
                          }))
                          setModulesPasteOpen(false)
                          setModulesPasteText('')
                        }}
                        data-cy="public-marketplace-apply-paste-modules"
                      >
                        {t('common:publicMarketplacePasteModulesApply')}
                      </Button>
                    </div>
                  </div>
                )}

                {form.modules.map((module, index) => (
                  <div key={module.id || `module-${index}`} className="space-y-3">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted">
                        {String(index + 1).padStart(2, '0')}{' '}
                        {t('common:publicMarketplaceModule')} {index + 1}
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            modules: prev.modules.filter((_, i) => i !== index),
                          }))
                        }
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <Input
                      value={module.title}
                      placeholder={t('common:publicMarketplaceModuleTitle')}
                      onChange={(event) => {
                        const next = [...form.modules]
                        next[index] = {
                          ...next[index],
                          title: event.target.value,
                        }
                        setForm((prev) => ({ ...prev, modules: next }))
                      }}
                    />
                    <Textarea
                      value={module.description || ''}
                      placeholder={t(
                        'common:publicMarketplaceModuleDescription',
                      )}
                      className="focus:outline-none ring-0 focus-visible:outline-none border-colorBorder min-h-[80px]"
                      onChange={(event) => {
                        const next = [...form.modules]
                        next[index] = {
                          ...next[index],
                          description: event.target.value,
                        }
                        setForm((prev) => ({ ...prev, modules: next }))
                      }}
                    />
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <Label>{t('common:publicMarketplaceModuleSubtopics')}</Label>
                        {(module.subtopics ?? []).length === 0 && (
                          <button
                            type="button"
                            className="bg-elevated px-4 py-2 rounded-lg text-sm text-colorText h-9 inline-flex items-center gap-2 shrink-0"
                            onClick={() => {
                              const next = [...form.modules]
                              next[index] = {
                                ...next[index],
                                subtopics: [''],
                              }
                              setForm((prev) => ({ ...prev, modules: next }))
                            }}
                          >
                            <Plus className="h-4 w-4" />
                            {t('common:publicMarketplaceAddSubtopic')}
                          </button>
                        )}
                      </div>
                      {(module.subtopics ?? []).map((subtopic, subtopicIndex) => (
                        <div
                          key={`module-${index}-subtopic-${subtopicIndex}`}
                          className="flex gap-2"
                        >
                          <Input
                            value={subtopic}
                            placeholder={t(
                              'common:publicMarketplaceModuleSubtopic',
                            )}
                            onChange={(event) => {
                              const next = [...form.modules]
                              const subtopics = [
                                ...(next[index].subtopics ?? []),
                              ]
                              subtopics[subtopicIndex] = event.target.value
                              next[index] = {
                                ...next[index],
                                subtopics,
                              }
                              setForm((prev) => ({ ...prev, modules: next }))
                            }}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="shrink-0"
                            aria-label="Remover"
                            onClick={() => {
                              const next = [...form.modules]
                              next[index] = {
                                ...next[index],
                                subtopics: (next[index].subtopics ?? []).filter(
                                  (_, i) => i !== subtopicIndex,
                                ),
                              }
                              setForm((prev) => ({ ...prev, modules: next }))
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          {subtopicIndex ===
                            (module.subtopics ?? []).length - 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="shrink-0"
                              aria-label={t(
                                'common:publicMarketplaceAddSubtopic',
                              )}
                              onClick={() => {
                                const next = [...form.modules]
                                next[index] = {
                                  ...next[index],
                                  subtopics: [
                                    ...(next[index].subtopics ?? []),
                                    '',
                                  ],
                                }
                                setForm((prev) => ({ ...prev, modules: next }))
                              }}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </section>
            )}
          </Card>
        </section>

        <section className="gap-6 justify-between m-0 w-full flex-col lg:flex-row flex mt-12">
          <article className="space-y-1 lg:w-[328px]">
            <h2 className="text-colorText font-medium">
              {t('common:publicMarketplaceProducerSection')}
            </h2>
            <p className="text-muted text-sm">
              {t('common:publicMarketplaceProducerSectionHelper')}
            </p>
          </article>

          <Card className="w-full p-4 lg:p-6 lg:max-w-[680px] bg-[09090b]">
            {isLoading ? (
              <RenderSkeleton count={4} />
            ) : (
              <section className="space-y-6">
                <article className="flex items-center gap-4">
                  <Avatar key={producerPhotoSrc || 'empty'} className="h-14 w-14 bg-zinc-700">
                    <AvatarImage
                      src={producerPhotoSrc}
                      className="h-full w-full object-cover"
                    />
                    <AvatarFallback className="bg-zinc-700 text-xl font-medium text-zinc-100">
                      {userInitials(sellerName || product?.name || 'MP')}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <label
                      htmlFor="public-marketplace-photo"
                      className={`bg-elevated px-4 py-2 rounded-lg text-sm text-colorText h-9 inline-flex items-center ${
                        isUploadingPhoto
                          ? 'opacity-50 cursor-not-allowed'
                          : 'cursor-pointer'
                      }`}
                    >
                      {isUploadingPhoto ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t('common:uploading')}
                        </>
                      ) : (
                        t('common:publicMarketplaceChangePhoto')
                      )}
                    </label>
                    <input
                      id="public-marketplace-photo"
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={isUploadingPhoto}
                      onChange={handlePhotoChange}
                    />
                    <p className="text-muted text-xs mt-2 max-w-sm">
                      {t('common:publicMarketplacePhotoHelper')}
                    </p>
                  </div>
                </article>

                <div className="space-y-2">
                  <Label htmlFor="public-marketplace-biography">
                    {t('common:publicMarketplaceBiography')}
                  </Label>
                  <Textarea
                    id="public-marketplace-biography"
                    value={form.biography}
                    className="focus:outline-none ring-0 focus-visible:outline-none border-colorBorder min-h-[120px]"
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        biography: event.target.value,
                      }))
                    }
                  />
                </div>

                <div className="flex gap-6 flex-col xl:flex-row">
                  <Input
                    label="Instagram"
                    value={form.instagram_url}
                    placeholder="https://"
                    onFocus={(event) => {
                      if (!event.target.value) {
                        setForm((prev) => ({
                          ...prev,
                          instagram_url: 'https://',
                        }))
                      }
                    }}
                    onPaste={(event) => {
                      const finalValue = normalizeUrlWhenPaste(event)
                      setForm((prev) => ({
                        ...prev,
                        instagram_url: finalValue,
                      }))
                    }}
                    onBlur={(event) => {
                      const normalized = normalizeUrlOnBlur(event.target.value)
                      setForm((prev) => ({
                        ...prev,
                        instagram_url: normalized,
                      }))
                    }}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        instagram_url: event.target.value,
                      }))
                    }
                  />
                  <Input
                    label="Facebook"
                    value={form.facebook_url}
                    placeholder="https://"
                    onFocus={(event) => {
                      if (!event.target.value) {
                        setForm((prev) => ({
                          ...prev,
                          facebook_url: 'https://',
                        }))
                      }
                    }}
                    onPaste={(event) => {
                      const finalValue = normalizeUrlWhenPaste(event)
                      setForm((prev) => ({
                        ...prev,
                        facebook_url: finalValue,
                      }))
                    }}
                    onBlur={(event) => {
                      const normalized = normalizeUrlOnBlur(event.target.value)
                      setForm((prev) => ({
                        ...prev,
                        facebook_url: normalized,
                      }))
                    }}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        facebook_url: event.target.value,
                      }))
                    }
                  />
                  <Input
                    label="YouTube"
                    value={form.youtube_url}
                    placeholder="https://"
                    onFocus={(event) => {
                      if (!event.target.value) {
                        setForm((prev) => ({
                          ...prev,
                          youtube_url: 'https://',
                        }))
                      }
                    }}
                    onPaste={(event) => {
                      const finalValue = normalizeUrlWhenPaste(event)
                      setForm((prev) => ({
                        ...prev,
                        youtube_url: finalValue,
                      }))
                    }}
                    onBlur={(event) => {
                      const normalized = normalizeUrlOnBlur(event.target.value)
                      setForm((prev) => ({
                        ...prev,
                        youtube_url: normalized,
                      }))
                    }}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        youtube_url: event.target.value,
                      }))
                    }
                  />
                </div>
              </section>
            )}
          </Card>
        </section>

        <footer className="w-full border-t border-t-colorBorder bg-black fixed bottom-0 left-0 p-4 lg:p-6">
          <div className="flex w-full md:justify-end justify-center gap-3 items-center">
            <Button
              type="button"
              variant="outline"
              className="w-full md:max-w-[180px]"
              disabled={isLoading}
              onClick={() => setPreviewOpen(true)}
            >
              <Eye className="h-4 w-4" />
              {t('common:publicMarketplacePreview')}
            </Button>
            <Button
              variant="default"
              className="w-full md:max-w-[148px]"
              disabled={isSaving || isLoading}
              data-cy="btn-save-public-marketplace"
              onClick={handleSave}
            >
              {isSaving ? (
                <Loader2 className="animate-spin" />
              ) : (
                t('common:save_changes')
              )}
            </Button>
          </div>
        </footer>
      </section>

      <PublicMarketplacePreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        productName={product?.name || ''}
        productImageUrl={productCoverSrc}
        sellerName={sellerName}
        categoryName={categoryName}
        description={description}
        advantages={form.advantages}
        modules={form.modules}
        biography={form.biography}
        photoUrl={producerPhotoSrc || null}
        details={previewDetails}
      />
    </>
  )
}
